import type { Time } from "@suse-o11y/date-time/time";
import { max } from "@suse-o11y/max";
import { min } from "@suse-o11y/min";
import type { Record } from "@suse-o11y/record";
import { round } from "@suse-o11y/round";
import { sortBy } from "@suse-o11y/sort-by";
import { timeToPx } from "@suse-o11y/time-to-px";
import { toArray } from "@suse-o11y/to-array";

import { Api } from "../../common/openapi";

import { SegmentEdge } from "./segment";

interface HealthSegmentBuilderParams {
	gap: number;
	minWidth: number;
}

export interface HealthChange {
	timestamp: number;
	newHealth: Api.HealthStateValue | undefined;
}

const HEALTH_STATE_TO_SEVERITY = {
	[Api.HealthStateValue.UNKNOWN]: 0,
	[Api.HealthStateValue.UNINITIALIZED]: 1,
	[Api.HealthStateValue.CLEAR]: 2,
	[Api.HealthStateValue.DISABLED]: 3,
	[Api.HealthStateValue.DEVIATING]: 4,
	[Api.HealthStateValue.FLAPPING]: 5,
	[Api.HealthStateValue.CRITICAL]: 6,
} satisfies Record<Api.HealthStateValue>;

function severityDiff(left: Api.HealthStateValue, right: Api.HealthStateValue): number {
	return HEALTH_STATE_TO_SEVERITY[left] - HEALTH_STATE_TO_SEVERITY[right];
}

export interface HealthSegment {
	health: Api.HealthStateValue | undefined;
	leftPx: number;
	rightPx: number;
}

export interface GetHealthSegmentsParams extends HealthSegmentBuilderParams {
	healthChanges: ReadonlyArray<HealthChange>;
	timelineStart: Time;
	timelineEnd: Time;
	timelineWidth: number;
}

function mergeHealthSegments(
	{ gap, minWidth }: HealthSegmentBuilderParams,
	current: HealthSegment,
	next: HealthSegment,
): ReadonlyArray<HealthSegment> {
	if (next.leftPx >= current.leftPx) {
		return [current];
	}

	const maxSegmentLeftPx = current.rightPx - minWidth;

	// if the head segment is shorter than the minimal width
	if (maxSegmentLeftPx < current.leftPx) {
		// the incoming segment merges to the head segment to make it at least minimal width long
		return mergeHealthSegments(
			{ gap, minWidth },
			{
				...current,
				// take the health that is the most severe of the two
				health:
					severityDiff(current.health ?? Api.HealthStateValue.UNKNOWN, next.health ?? Api.HealthStateValue.UNKNOWN) > 0
						? current.health
						: next.health,

				// the head segment extends to the left edge of the incoming segment but not further than the minimal width
				leftPx: max(next.leftPx, maxSegmentLeftPx),
			},
			next,
		);
	}

	const shiftedSegmentLeftPx = min(
		// desired minimal right edge of the incoming segment
		next.leftPx + minWidth + gap,
		// the head segment cannot shrink below the min width
		maxSegmentLeftPx,
	);

	if (
		// if the incoming segment has higher severity
		severityDiff(next.health ?? Api.HealthStateValue.UNKNOWN, current.health ?? Api.HealthStateValue.UNKNOWN) > 0 &&
		// AND the head segment can shrink
		shiftedSegmentLeftPx > current.leftPx
	) {
		// the head segment shrinks to release space for the incoming more severe segment
		return mergeHealthSegments({ gap, minWidth }, { ...current, leftPx: shiftedSegmentLeftPx }, next);
	}

	// creates a new segment with the gap to the left of the head segment
	return [{ ...next, rightPx: current.leftPx - gap }, current];
}

/**
 * Quantify health segments such that each segment is at least 2x the HEALTH_LINE_GAP_PX
 * Do this by extending segments if possible (start / end shift at most 1.5 GAP_PX) or dropping them otherwise
 */
export const getHealthSegments = ({
	healthChanges,
	timelineStart,
	timelineEnd,
	timelineWidth,
	gap,
	minWidth,
}: GetHealthSegmentsParams): Array<HealthSegment> => {
	const segments = sortBy(healthChanges, [({ timestamp }) => timestamp, "desc"]).reduce(
		(itemBuilder, { timestamp, newHealth }) => {
			const leftPx = timeToPx({
				time: timestamp,
				width: timelineWidth,
				interval: { start: timelineStart, end: timelineEnd },
			});

			return itemBuilder.shift({ health: newHealth, leftPx: round(leftPx, 0), rightPx: timelineWidth });
		},
		new SegmentEdge<HealthSegment>((current, next) => mergeHealthSegments({ gap, minWidth }, current, next)),
	);

	return toArray(
		segments.shift({
			health: healthChanges.length === 0 ? Api.HealthStateValue.UNKNOWN : undefined,
			leftPx: 0,
			rightPx: timelineWidth,
		}),
	);
};
